import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Loader2, Plus, Trash2, Check, X, Calendar, CreditCard, ShieldCheck, User, Star, MessageSquare, Send, Paperclip, FileText } from "lucide-react";
import {
  adminGetCustomerDetail,
  adminUpsertAppointment, adminDeleteAppointment, adminAppointmentDecision,
  adminUpsertPayment, adminUpsertWarranty, adminUpdateClaim, adminUpdateProjectProfile,
  adminListMessages, adminSendMessage, adminMarkMessagesRead, adminGetMessageFileSignedUrl,
} from "@/lib/portal/admin.functions";
import { createReviewRequest } from "@/lib/reviews/reviews.functions";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/customers/$projectId")({
  component: CustomerDetailPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="py-20 text-center">
        <p className="text-red-400 mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="text-zinc-400">Not found.</div>,
});

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 2 });

function RequestReviewButton({ projectId }: { projectId: string }) {
  const fn = useServerFn(createReviewRequest);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const res: any = await fn({ data: { projectId, triggerSource: "manual", origin: window.location.origin } });
      if (res?.token) {
        toast.success("Review request sent", { description: `Token: ${res.token.slice(0, 12)}…` });
      } else {
        toast.message("Review request created", { description: "Email skipped (Resend not configured)." });
      }
    } catch (e) {
      toast.error((e as Error).message);
    } finally { setBusy(false); }
  };
  return (
    <button onClick={send} disabled={busy}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-amber-300/10 border border-amber-300/30 text-amber-200 hover:bg-amber-300/20 disabled:opacity-50">
      {busy ? <Loader2 className="size-4 animate-spin" /> : <Star className="size-4" />} Request Review
    </button>
  );
}

const TABS = [
  { id: "schedule", label: "Schedule", icon: Calendar },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "warranty", label: "Warranty", icon: ShieldCheck },
  { id: "profile",  label: "Profile",  icon: User },
] as const;
type TabId = typeof TABS[number]["id"];

function CustomerDetailPage() {
  const { projectId } = Route.useParams();
  const getFn = useServerFn(adminGetCustomerDetail);
  const q = useQuery({
    queryKey: ["admin-customer", projectId],
    queryFn: () => getFn({ data: { projectId } }),
  });
  const initialTab = ((): TabId => {
    if (typeof window === "undefined") return "schedule";
    const t = new URLSearchParams(window.location.search).get("tab");
    return (TABS.some(x => x.id === t) ? t : "schedule") as TabId;
  })();
  const [tab, setTab] = useState<TabId>(initialTab);
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("tab") !== tab) {
      url.searchParams.set("tab", tab);
      window.history.replaceState({}, "", url.toString());
    }
  }, [tab]);

  if (q.isLoading) return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-zinc-400" /></div>;
  if (!q.data?.project) return <div className="text-zinc-400">Project not found.</div>;

  const d = q.data!;
  const p = d.project as any;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/customers" className="text-xs text-zinc-500 hover:text-amber-300 inline-flex items-center gap-1">
          <ArrowLeft className="size-3" /> Back to customers
        </Link>
        <h1 className="font-serif text-3xl text-white mt-3">{p.project_name}</h1>
        <p className="text-sm text-zinc-400 mt-1">{p.customer_name} · {p.customer_email}{p.customer_phone ? ` · ${p.customer_phone}` : ""}</p>
        <p className="text-xs text-zinc-500">{[p.address, p.city, p.state, p.zip].filter(Boolean).join(", ") || "No address on file"}</p>
      </div>
      <RequestReviewButton projectId={projectId} />

      <div className="flex gap-1.5 border-b border-white/10">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 transition ${tab === t.id ? "border-amber-300 text-white" : "border-transparent text-zinc-500 hover:text-zinc-200"}`}>
              <Icon className="size-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "schedule" && <ScheduleAdmin projectId={projectId} appointments={d.appointments} />}
      {tab === "messages" && <MessagesAdmin projectId={projectId} />}
      {tab === "payments" && <PaymentsAdmin projectId={projectId} payments={d.payments} project={p} />}
      {tab === "warranty" && <WarrantyAdmin projectId={projectId} warranty={d.warranty} claims={d.claims} />}
      {tab === "profile" && <ProfileAdmin projectId={projectId} project={p} preferences={d.preferences} />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// SCHEDULE
// -----------------------------------------------------------------------------
function ScheduleAdmin({ projectId, appointments }: { projectId: string; appointments: any[] }) {
  const qc = useQueryClient();
  const upsertFn = useServerFn(adminUpsertAppointment);
  const delFn = useServerFn(adminDeleteAppointment);
  const decisionFn = useServerFn(adminAppointmentDecision);
  const [editing, setEditing] = useState<any | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-customer", projectId] });

  const decide = async (appt: any, decision: "approve" | "deny") => {
    let newStartsAt: string | undefined;
    if (decision === "approve" && appt.customer_response) {
      try {
        const r = JSON.parse(appt.customer_response);
        if (r.proposed_at) newStartsAt = new Date(r.proposed_at).toISOString();
      } catch {}
    }
    await decisionFn({ data: { appointmentId: appt.id, decision, newStartsAt } });
    refresh();
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button onClick={() => setEditing({ projectId, kind: "estimate", title: "", starts_at: "", status: "scheduled" })}
          className="bg-amber-300 text-zinc-950 text-xs font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5">
          <Plus className="size-3.5" /> Add appointment
        </button>
      </div>
      {appointments.length === 0 ? (
        <p className="text-sm text-zinc-500 py-10 text-center border border-white/10 rounded-2xl">No appointments yet.</p>
      ) : appointments.map((a) => {
        let proposed: any = null;
        if (a.customer_response) { try { proposed = JSON.parse(a.customer_response); } catch {} }
        return (
          <div key={a.id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-amber-300 font-semibold">{a.kind}</p>
                <p className="text-white">{a.title}</p>
                <p className="text-xs text-zinc-400 mt-0.5">{new Date(a.starts_at).toLocaleString()}{a.location ? ` · ${a.location}` : ""}</p>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{a.status.replace("_"," ")}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(a)} className="text-xs text-zinc-300 border border-white/10 rounded-full px-3 py-1.5">Edit</button>
                <button onClick={async () => { if(confirm("Delete?")){ await delFn({ data: { id: a.id } }); refresh(); } }}
                  className="text-xs text-red-300 border border-red-500/20 rounded-full px-3 py-1.5 inline-flex items-center gap-1"><Trash2 className="size-3" /></button>
              </div>
            </div>
            {proposed?.proposed_at && (
              <div className="mt-3 rounded-xl bg-amber-300/5 border border-amber-300/20 p-3 text-xs">
                <p className="text-amber-200">Customer requested: <strong>{new Date(proposed.proposed_at).toLocaleString()}</strong></p>
                {proposed.note && <p className="text-zinc-400 mt-1">"{proposed.note}"</p>}
                <div className="mt-2 flex gap-2">
                  <button onClick={() => decide(a, "approve")} className="text-[11px] bg-emerald-400 text-zinc-950 font-semibold rounded-full px-3 py-1 inline-flex items-center gap-1"><Check className="size-3" /> Approve new time</button>
                  <button onClick={() => decide(a, "deny")} className="text-[11px] border border-white/10 text-zinc-200 rounded-full px-3 py-1 inline-flex items-center gap-1"><X className="size-3" /> Keep original</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {editing && <ApptModal projectId={projectId} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} upsertFn={upsertFn} />}
    </div>
  );
}

function ApptModal({ projectId, initial, onClose, onSaved, upsertFn }: any) {
  const [form, setForm] = useState({
    id: initial.id, projectId, kind: initial.kind || "estimate",
    title: initial.title || "", starts_at: initial.starts_at ? toLocalInput(initial.starts_at) : "",
    ends_at: initial.ends_at ? toLocalInput(initial.ends_at) : "",
    location: initial.location || "", notes: initial.notes || "", status: initial.status || "scheduled",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await upsertFn({ data: {
        ...(form.id ? { id: form.id } : {}),
        projectId, kind: form.kind, title: form.title,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : undefined,
        location: form.location || undefined, notes: form.notes || undefined, status: form.status,
      } });
      onSaved();
    } catch (e: any) { alert(e?.message || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title={form.id ? "Edit appointment" : "New appointment"}>
      <div className="grid grid-cols-2 gap-3">
        <FieldA label="Kind"><select className={inputCls} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
          {["estimate","color_consult","start","paint_day","walkthrough","other"].map(k => <option key={k} value={k}>{k}</option>)}
        </select></FieldA>
        <FieldA label="Status"><select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
          {["scheduled","confirmed","reschedule_requested","cancelled","completed"].map(k => <option key={k} value={k}>{k}</option>)}
        </select></FieldA>
        <FieldA label="Title" full><input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></FieldA>
        <FieldA label="Starts"><input type="datetime-local" className={inputCls} value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} /></FieldA>
        <FieldA label="Ends (optional)"><input type="datetime-local" className={inputCls} value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} /></FieldA>
        <FieldA label="Location" full><input className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></FieldA>
        <FieldA label="Notes" full><textarea rows={3} className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></FieldA>
      </div>
      <ModalActions onClose={onClose} onSubmit={submit} busy={busy} disabled={!form.title || !form.starts_at} />
    </Modal>
  );
}

function toLocalInput(iso: string) {
  const d = new Date(iso); const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// -----------------------------------------------------------------------------
// PAYMENTS
// -----------------------------------------------------------------------------
function PaymentsAdmin({ projectId, payments, project }: { projectId: string; payments: any[]; project: any }) {
  const qc = useQueryClient();
  const fn = useServerFn(adminUpsertPayment);
  const [editing, setEditing] = useState<any | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-customer", projectId] });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SmallStat label="Total" value={fmt(Number(project.total_amount ?? 0))} />
        <SmallStat label="Deposit" value={fmt(Number(project.deposit_amount ?? 0))} />
        <SmallStat label="Paid" value={fmt(Number(project.amount_paid ?? 0))} />
        <SmallStat label="Balance" value={fmt(Number(project.balance_due ?? 0))} />
      </div>
      <div className="flex justify-end">
        <button onClick={() => setEditing({ projectId, kind: "deposit", amount: 0, method: "check", provider: "manual", status: "received" })}
          className="bg-amber-300 text-zinc-950 text-xs font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5">
          <Plus className="size-3.5" /> Record payment
        </button>
      </div>
      {payments.length === 0 ? (
        <p className="text-sm text-zinc-500 py-10 text-center border border-white/10 rounded-2xl">No payments recorded.</p>
      ) : payments.map(r => (
        <div key={r.id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-white capitalize">{r.kind.replace("_"," ")} — {fmt(Number(r.amount))}</p>
            <p className="text-xs text-zinc-500">{new Date(r.recorded_at).toLocaleString()} · {r.method} · {r.status}{r.reference ? ` · ${r.reference}` : ""}</p>
          </div>
          <button onClick={() => setEditing(r)} className="text-xs text-zinc-300 border border-white/10 rounded-full px-3 py-1.5">Edit</button>
        </div>
      ))}
      {editing && <PaymentModal projectId={projectId} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} fn={fn} />}
    </div>
  );
}

function PaymentModal({ projectId, initial, onClose, onSaved, fn }: any) {
  const [form, setForm] = useState({
    id: initial.id, projectId,
    kind: initial.kind || "deposit",
    amount: Number(initial.amount ?? 0),
    method: initial.method || "check",
    provider: initial.provider || "manual",
    reference: initial.reference || "",
    status: initial.status || "received",
    notes: initial.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await fn({ data: {
        ...(form.id ? { id: form.id } : {}),
        projectId, kind: form.kind, amount: form.amount,
        method: form.method, provider: form.provider,
        reference: form.reference || undefined, status: form.status,
        notes: form.notes || undefined,
      } });
      onSaved();
    } catch (e: any) { alert(e?.message || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title={form.id ? "Edit payment" : "Record payment"}>
      <div className="grid grid-cols-2 gap-3">
        <FieldA label="Kind"><select className={inputCls} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
          {["deposit","progress","final","refund","other"].map(k => <option key={k} value={k}>{k}</option>)}
        </select></FieldA>
        <FieldA label="Status"><select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
          {["pending","received","refunded","failed"].map(k => <option key={k} value={k}>{k}</option>)}
        </select></FieldA>
        <FieldA label="Amount (USD)"><input type="number" step="0.01" className={inputCls} value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} /></FieldA>
        <FieldA label="Method"><input className={inputCls} value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} placeholder="check, ach, card, cash" /></FieldA>
        <FieldA label="Provider"><input className={inputCls} value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} /></FieldA>
        <FieldA label="Reference"><input className={inputCls} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="check #, txn id" /></FieldA>
        <FieldA label="Notes" full><textarea rows={2} className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></FieldA>
      </div>
      <ModalActions onClose={onClose} onSubmit={submit} busy={busy} />
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// WARRANTY
// -----------------------------------------------------------------------------
function WarrantyAdmin({ projectId, warranty, claims }: { projectId: string; warranty: any; claims: any[] }) {
  const qc = useQueryClient();
  const upsertFn = useServerFn(adminUpsertWarranty);
  const claimFn = useServerFn(adminUpdateClaim);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-customer", projectId] });

  const [form, setForm] = useState({
    coverage_years: 3, starts_on: new Date().toISOString().slice(0,10),
    expires_on: "", document_url: "", details: "",
  });
  useEffect(() => {
    if (warranty) setForm({
      coverage_years: warranty.coverage_years ?? 3,
      starts_on: warranty.starts_on ?? new Date().toISOString().slice(0,10),
      expires_on: warranty.expires_on ?? "",
      document_url: warranty.document_url ?? "",
      details: warranty.details ?? "",
    });
  }, [warranty]);

  const save = async () => {
    await upsertFn({ data: {
      projectId, coverage_years: form.coverage_years, starts_on: form.starts_on,
      expires_on: form.expires_on || undefined, document_url: form.document_url || undefined,
      details: form.details || undefined,
    } });
    refresh();
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
        <h3 className="text-sm uppercase tracking-wider text-zinc-500 font-semibold">Warranty terms</h3>
        <div className="grid grid-cols-2 gap-3">
          <FieldA label="Coverage years"><input type="number" min={0} className={inputCls} value={form.coverage_years} onChange={(e) => setForm({ ...form, coverage_years: Number(e.target.value) })} /></FieldA>
          <FieldA label="Document URL"><input className={inputCls} value={form.document_url} onChange={(e) => setForm({ ...form, document_url: e.target.value })} /></FieldA>
          <FieldA label="Starts on"><input type="date" className={inputCls} value={form.starts_on} onChange={(e) => setForm({ ...form, starts_on: e.target.value })} /></FieldA>
          <FieldA label="Expires on"><input type="date" className={inputCls} value={form.expires_on} onChange={(e) => setForm({ ...form, expires_on: e.target.value })} /></FieldA>
          <FieldA label="Details" full><textarea rows={4} className={inputCls} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} /></FieldA>
        </div>
        <div className="flex justify-end">
          <button onClick={save} className="bg-amber-300 text-zinc-950 text-xs font-semibold px-5 py-2 rounded-full">Save warranty</button>
        </div>
      </div>

      <h3 className="text-sm uppercase tracking-wider text-zinc-500 font-semibold pt-2">Customer warranty requests</h3>
      {claims.length === 0 ? (
        <p className="text-sm text-zinc-500 py-10 text-center border border-white/10 rounded-2xl">No requests submitted.</p>
      ) : claims.map((c) => <ClaimAdminCard key={c.id} claim={c} onUpdate={async (status, notes) => { await claimFn({ data: { claimId: c.id, status, admin_notes: notes } }); refresh(); }} />)}
    </div>
  );
}

function ClaimAdminCard({ claim, onUpdate }: { claim: any; onUpdate: (s: any, n: string) => Promise<void> }) {
  const [status, setStatus] = useState(claim.status);
  const [notes, setNotes] = useState(claim.admin_notes ?? "");
  const [busy, setBusy] = useState(false);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      <p className="text-xs text-zinc-500">{new Date(claim.created_at).toLocaleString()}</p>
      <p className="text-sm text-white whitespace-pre-wrap mt-1">{claim.description}</p>
      {Array.isArray(claim.photos) && claim.photos.length > 0 && (
        <p className="text-[11px] text-zinc-500 mt-2">{claim.photos.length} photo(s) attached</p>
      )}
      <div className="grid sm:grid-cols-3 gap-3 mt-3">
        <select className={inputCls} value={status} onChange={(e) => setStatus(e.target.value)}>
          {["submitted","in_review","scheduled","resolved","denied"].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <textarea rows={2} className={inputCls + " sm:col-span-2"} placeholder="Internal/customer notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <div className="flex justify-end mt-3">
        <button onClick={async () => { setBusy(true); await onUpdate(status, notes); setBusy(false); }}
          className="bg-amber-300 text-zinc-950 text-xs font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5">
          {busy && <Loader2 className="size-3 animate-spin" />} Update
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// PROFILE
// -----------------------------------------------------------------------------
function ProfileAdmin({ projectId, project, preferences }: { projectId: string; project: any; preferences: any }) {
  const qc = useQueryClient();
  const fn = useServerFn(adminUpdateProjectProfile);
  const [form, setForm] = useState({
    customer_name: project.customer_name ?? "", customer_email: project.customer_email ?? "",
    customer_phone: project.customer_phone ?? "", address: project.address ?? "",
    city: project.city ?? "", state: project.state ?? "", zip: project.zip ?? "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await fn({ data: { projectId, ...form } });
      qc.invalidateQueries({ queryKey: ["admin-customer", projectId] });
    } catch (e: any) { alert(e?.message || "Save failed"); }
    finally { setBusy(false); }
  };

  const sc = (preferences?.secondary_contact ?? {}) as any;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
        <h3 className="text-sm uppercase tracking-wider text-zinc-500 font-semibold">Project profile</h3>
        <div className="grid grid-cols-2 gap-3">
          <FieldA label="Customer name"><input className={inputCls} value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></FieldA>
          <FieldA label="Phone"><input className={inputCls} value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} /></FieldA>
          <FieldA label="Email" full><input className={inputCls} type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></FieldA>
          <FieldA label="Address" full><input className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></FieldA>
          <FieldA label="City"><input className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></FieldA>
          <FieldA label="State"><input className={inputCls} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></FieldA>
          <FieldA label="ZIP"><input className={inputCls} value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} /></FieldA>
        </div>
        <div className="flex justify-end">
          <button onClick={submit} disabled={busy} className="bg-amber-300 text-zinc-950 text-xs font-semibold px-5 py-2 rounded-full inline-flex items-center gap-1.5">
            {busy && <Loader2 className="size-3 animate-spin" />} Save profile
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <h3 className="text-sm uppercase tracking-wider text-zinc-500 font-semibold">Customer-side preferences</h3>
        {!preferences ? (
          <p className="text-sm text-zinc-500 mt-3">Customer hasn't set preferences yet.</p>
        ) : (
          <div className="mt-3 text-sm text-zinc-300 space-y-1">
            <p>Display name: <span className="text-white">{preferences.display_name || "—"}</span></p>
            <p>Phone: <span className="text-white">{preferences.phone || "—"}</span></p>
            <p>Notifications: <span className="text-white">{[
              preferences.comm_email && "email", preferences.comm_sms && "sms", preferences.comm_push && "in-app"
            ].filter(Boolean).join(", ") || "none"}</span></p>
            <p>Secondary contact: <span className="text-white">{sc.name ? `${sc.name}${sc.relationship ? ` (${sc.relationship})` : ""} — ${sc.phone || sc.email || ""}` : "—"}</span></p>
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// shared
// -----------------------------------------------------------------------------
const inputCls = "w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:border-amber-300/50 outline-none";

function FieldA({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <label className={`block ${full ? "col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function SmallStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
      <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className="text-white text-lg font-serif mt-0.5">{value}</p>
    </div>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur grid place-items-center p-4" onClick={onClose}>
      <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-[#0a0a0a] p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500"><X className="size-5" /></button>
        <h2 className="font-serif text-2xl text-white mb-4">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ onClose, onSubmit, busy, disabled }: { onClose: () => void; onSubmit: () => void; busy: boolean; disabled?: boolean }) {
  return (
    <div className="mt-5 flex justify-end gap-2">
      <button onClick={onClose} className="text-sm text-zinc-400 px-4 py-2">Cancel</button>
      <button onClick={onSubmit} disabled={busy || disabled}
        className="bg-amber-300 text-zinc-950 text-sm font-semibold px-5 py-2 rounded-full inline-flex items-center gap-2 disabled:opacity-50">
        {busy && <Loader2 className="size-4 animate-spin" />} Save
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// MESSAGES (admin <-> customer chat)
// -----------------------------------------------------------------------------
function MessagesAdmin({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const listFn = useServerFn(adminListMessages);
  const sendFn = useServerFn(adminSendMessage);
  const markFn = useServerFn(adminMarkMessagesRead);
  const q = useQuery({
    queryKey: ["admin-messages", projectId],
    queryFn: () => listFn({ data: { projectId } }),
    refetchInterval: 10000,
  });
  const [body, setBody] = useState("");
  const [pending, setPending] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    markFn({ data: { projectId } }).catch(() => {});
  }, [projectId, q.data?.messages.length]);

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight });
  }, [q.data?.messages.length]);

  const send = async () => {
    if (!body.trim() && pending.length === 0) return;
    setSending(true);
    try {
      const attachments: { url: string; name: string; mime?: string }[] = [];
      for (const file of pending) {
        const ext = file.name.split(".").pop()?.toLowerCase() || "bin";
        const path = `${projectId}/messages/${crypto.randomUUID()}.${ext}`;
        const { error } = await supabase.storage.from("project-files").upload(path, file, { contentType: file.type });
        if (error) throw error;
        attachments.push({ url: path, name: file.name, mime: file.type });
      }
      await sendFn({ data: { projectId, body: body.trim() || undefined, attachments: attachments.length ? attachments : undefined } });
      setBody(""); setPending([]);
      await qc.invalidateQueries({ queryKey: ["admin-messages", projectId] });
    } catch (e: any) {
      toast.error(e?.message || "Failed to send");
    } finally { setSending(false); }
  };

  if (q.isLoading) return <div className="py-16 grid place-items-center"><Loader2 className="size-5 animate-spin text-amber-300" /></div>;
  const messages = q.data?.messages ?? [];
  const me = q.data?.me;

  return (
    <div className="flex flex-col h-[70vh]">
      <div ref={scrollerRef} className="flex-1 overflow-y-auto rounded-2xl border border-white/10 bg-black/30 p-4 space-y-3">
        {messages.length === 0 ? (
          <p className="text-center text-sm text-zinc-500 py-12">No messages yet. Say hi 👋</p>
        ) : messages.map((m) => {
          const mine = m.sender_user_id === me;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                mine ? "bg-amber-300 text-zinc-950" : "bg-white/[0.06] text-zinc-100 border border-white/10"
              }`}>
                {!mine && <p className="text-[10px] uppercase tracking-wider text-amber-300 mb-1 font-semibold">{m.sender_name ?? "Customer"}</p>}
                {m.body && <p className="whitespace-pre-wrap leading-relaxed">{m.body}</p>}
                {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {m.attachments.map((a: any, i: number) => <AdminMessageAttachment key={i} projectId={projectId} att={a} mine={mine} />)}
                  </div>
                )}
                <p className={`text-[10px] mt-1 ${mine ? "text-zinc-700" : "text-zinc-500"}`}>{new Date(m.created_at).toLocaleString([], { hour: "numeric", minute: "2-digit", month: "short", day: "numeric" })}</p>
              </div>
            </div>
          );
        })}
      </div>

      {pending.length > 0 && (
        <div className="flex gap-2 flex-wrap mt-2">
          {pending.map((f, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1 text-[11px] text-zinc-300">
              {f.name}
              <button onClick={() => setPending(pending.filter((_, j) => j !== i))}><X className="size-3" /></button>
            </span>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-end gap-2 rounded-2xl border border-white/10 bg-white/[0.02] p-2">
        <label className="size-10 rounded-xl bg-white/5 hover:bg-white/10 grid place-items-center cursor-pointer shrink-0">
          <Paperclip className="size-4 text-zinc-300" />
          <input type="file" multiple accept="image/*,application/pdf" className="hidden"
            onChange={(e) => setPending([...pending, ...Array.from(e.target.files ?? [])])} />
        </label>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={1} placeholder="Reply to your customer…"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="flex-1 resize-none bg-transparent text-base text-white placeholder:text-zinc-500 px-3 py-2 focus:outline-none max-h-32" />
        <button onClick={send} disabled={sending || (!body.trim() && pending.length === 0)}
          className="size-10 rounded-xl bg-amber-300 hover:bg-amber-200 disabled:opacity-40 grid place-items-center text-zinc-950 shrink-0">
          {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
        </button>
      </div>
    </div>
  );
}

function AdminMessageAttachment({ projectId, att, mine }: { projectId: string; att: any; mine: boolean }) {
  const isHttp = /^https?:\/\//i.test(att.url);
  const signFn = useServerFn(adminGetMessageFileSignedUrl);
  const q = useQuery({
    queryKey: ["admin-msg-sign", projectId, att.url],
    queryFn: () => signFn({ data: { projectId, path: att.url } }),
    enabled: !isHttp,
    staleTime: 4 * 60 * 1000,
  });
  const url = isHttp ? att.url : q.data?.url;
  const isImage = (att.mime ?? "").startsWith("image/");
  if (isImage && url) {
    return <a href={url} target="_blank" rel="noopener"><img src={url} alt={att.name} className="rounded-lg max-h-48 max-w-full" /></a>;
  }
  return (
    <a href={url ?? "#"} target="_blank" rel="noopener"
      className={`inline-flex items-center gap-2 text-xs underline ${mine ? "text-zinc-800" : "text-amber-200"}`}>
      <FileText className="size-3.5" /> {att.name}
    </a>
  );
}
